(() => {
  if (window.BelvansProductWatchSwitcher) return;

  const ENGRAVING_SECTION_ID = 'api-product-engraving-interactive';
  const productCache = new Map();
  const engravingCache = new Map();
  const parser = new DOMParser();

  const normalizeProductUrl = (rawUrl) => {
    const url = new URL(rawUrl, window.location.href);
    url.search = '';
    url.hash = '';
    return url;
  };

  const buildSectionUrl = (rawUrl, sectionId) => {
    const url = normalizeProductUrl(rawUrl);

    if (sectionId) {
      url.searchParams.set('section_id', sectionId);
    }

    return url.toString();
  };

  const fetchSectionHtml = async (rawUrl, sectionId, cache) => {
    const cacheKey = `${normalizeProductUrl(rawUrl).pathname}::${sectionId || 'page'}`;

    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }

    const request = fetch(buildSectionUrl(rawUrl, sectionId), {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load ${sectionId} for ${rawUrl}`);
      }

      return response.text();
    });

    cache.set(cacheKey, request);
    return request;
  };

  const getProductInfoFragment = async (productUrl, productHandle) => {
    const html = await fetchSectionHtml(productUrl, null, productCache);
    const doc = parser.parseFromString(html, 'text/html');
    const productInfos = Array.from(doc.querySelectorAll('product-info'));
    const productInfo =
      productInfos.find((fragment) => fragment.dataset.productHandle === productHandle) || productInfos[0];

    if (!productInfo) {
      throw new Error(`Product info fragment missing for ${productUrl}`);
    }

    return productInfo;
  };

  const getEngravingFragment = async (productUrl) => {
    const html = await fetchSectionHtml(productUrl, ENGRAVING_SECTION_ID, engravingCache);
    const doc = parser.parseFromString(html, 'text/html');

    return doc.querySelector('[data-engraving-interactive-source]');
  };

  const cloneForInsert = (node) => (node ? node.cloneNode(true) : null);

  const blockFor = (node) => node?.closest('.product__block') || node;

  const replaceElement = (currentElement, sourceElement) => {
    const nextElement = cloneForInsert(sourceElement);

    if (!currentElement || !nextElement) return null;

    currentElement.replaceWith(nextElement);
    return nextElement;
  };

  const replaceBlockBySelector = (currentRoot, sourceRoot, selector) => {
    const currentElement = blockFor(currentRoot.querySelector(selector));
    const sourceElement = blockFor(sourceRoot.querySelector(selector));

    return replaceElement(currentElement, sourceElement);
  };

  const replaceAllBySelector = (currentRoot, sourceRoot, selector) => {
    const currentElements = Array.from(currentRoot.querySelectorAll(selector)).map(blockFor);
    const sourceElements = Array.from(sourceRoot.querySelectorAll(selector)).map(blockFor);

    currentElements.forEach((currentElement, index) => {
      const sourceElement = sourceElements[index];
      if (currentElement && sourceElement) {
        replaceElement(currentElement, sourceElement);
      }
    });
  };

  const getSlidePreviewHref = (productImages, mediaId) => {
    if (!productImages || !mediaId) return '#';

    const slide = productImages.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
    const zoomImage = slide?.querySelector('[data-zoom-image]');
    const image = slide?.querySelector('img');

    return zoomImage?.dataset.imageSrc || image?.currentSrc || image?.src || '#';
  };

  const buildStandardThumbs = (productImages) => {
    const sourceThumbItems = Array.from(productImages.querySelectorAll('product-thumbs [data-thumb-item]'));

    if (!sourceThumbItems.length) return null;

    const thumbsTemplate = document.createElement('template');

    thumbsTemplate.innerHTML = '<product-thumbs class="product__thumbs"><div class="product__thumbs__holder" data-thumbs-slider></div></product-thumbs>';

    const thumbs = thumbsTemplate.content.firstElementChild;
    const holder = thumbs?.querySelector('[data-thumbs-slider]');

    if (!thumbs || !holder) return null;

    holder.className = 'product__thumbs__holder';
    holder.setAttribute('data-thumbs-slider', '');

    sourceThumbItems.forEach((sourceItem, index) => {
      const sourceLink = sourceItem.querySelector('[data-thumb-link]');
      const mediaId = sourceLink?.dataset.mediaId || sourceLink?.dataset.dotMediaId || '';
      const isActive =
        sourceItem.classList.contains('is-active') ||
        sourceLink?.classList.contains('is-active') ||
        sourceLink?.getAttribute('aria-current') === 'true' ||
        index === 0;
      const thumb = document.createElement('div');
      const link = document.createElement('a');
      const sourceFigure =
        sourceLink?.querySelector('.product__mobile-dot__image') ||
        sourceLink?.querySelector('.image-wrapper') ||
        productImages.querySelector(`[data-media-id="${CSS.escape(mediaId)}"] .image-wrapper`);
      const figure = sourceFigure?.cloneNode(true);
      const badge = sourceLink?.querySelector('.product__mobile-dot__badge')?.cloneNode(true);

      thumb.className = `product__thumb${isActive ? ' is-active' : ''}`;
      thumb.setAttribute('data-thumb-item', '');

      link.className = 'product__thumb__link';
      link.href = sourceLink?.getAttribute('href') || getSlidePreviewHref(productImages, mediaId);
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
      link.setAttribute('data-thumb-link', '');

      if (mediaId) {
        link.dataset.mediaId = mediaId;
      }

      if (figure) {
        figure.classList.remove('product__mobile-dot__image');
        figure.classList.add('image-wrapper');
        link.appendChild(figure);
      }

      if (badge) {
        badge.classList.remove('product__mobile-dot__badge');
        badge.classList.add('product__thumb__badge');
        link.appendChild(badge);
      }

      thumb.appendChild(link);
      holder.appendChild(thumb);
    });

    thumbs.appendChild(holder);
    return thumbs;
  };

  const normalizeProductImagesForCurrentLayout = (currentImages, nextImages) => {
    if (!currentImages || !nextImages) return;

    nextImages.className = currentImages.className;
    nextImages.removeAttribute('data-museum-layout');

    ['data-fader-desktop', 'data-fader-mobile'].forEach((attribute) => {
      if (currentImages.hasAttribute(attribute)) {
        nextImages.setAttribute(attribute, '');
      } else {
        nextImages.removeAttribute(attribute);
      }
    });

    const sourceThumbs = nextImages.querySelector('product-thumbs');
    const shouldNormalizeThumbs =
      sourceThumbs?.classList.contains('product__mobile-dots') ||
      !sourceThumbs?.querySelector('.product__thumbs__holder');
    const standardThumbs = shouldNormalizeThumbs ? buildStandardThumbs(nextImages) : null;

    if (standardThumbs && sourceThumbs) {
      sourceThumbs.replaceWith(standardThumbs);
    } else if (standardThumbs) {
      nextImages.appendChild(standardThumbs);
    }
  };

  const replaceProductImages = (currentRoot, sourceRoot) => {
    const currentElement = blockFor(currentRoot.querySelector('product-images'));
    const sourceElement = blockFor(sourceRoot.querySelector('product-images'));
    const nextElement = cloneForInsert(sourceElement);

    if (!currentElement || !nextElement) return null;

    const currentImages = currentElement.matches?.('product-images')
      ? currentElement
      : currentElement.querySelector('product-images');
    const nextImages = nextElement.matches?.('product-images')
      ? nextElement
      : nextElement.querySelector('product-images');

    normalizeProductImagesForCurrentLayout(currentImages, nextImages);
    currentElement.replaceWith(nextElement);

    return nextElement;
  };

  const updateWatchSwitcherState = (currentInfo, sourceInfo, productHandle) => {
    const switcher = currentInfo.querySelector('.product__watch-switcher');
    if (!switcher) return;

    const sourceActiveItem = sourceInfo.querySelector('[data-watch-switcher-item].is-active');
    const sourceActiveLabel = sourceActiveItem?.querySelector('.product__watch-switcher__item-label')?.textContent?.trim();
    const sourceStrongLabel = sourceInfo.querySelector('.product__watch-switcher__label strong')?.textContent?.trim();
    const currentActiveItem = switcher.querySelector(`[data-watch-switcher-item][data-product-handle="${productHandle}"]`);
    const currentActiveLabel = currentActiveItem?.querySelector('.product__watch-switcher__item-label')?.textContent?.trim();
    const fallbackLabel = sourceInfo.querySelector('.product__title [data-zoom-caption]')?.textContent?.trim();
    const currentLabel = sourceStrongLabel || sourceActiveLabel || currentActiveLabel || fallbackLabel || '';

    switcher.querySelectorAll('[data-watch-switcher-item]').forEach((item) => {
      const isActive = item.dataset.productHandle === productHandle;

      item.classList.toggle('is-active', isActive);
      item.toggleAttribute('aria-current', isActive);

      if (isActive && !currentLabel) {
        const itemLabel = item.querySelector('.product__watch-switcher__item-label')?.textContent?.trim();
        const labelTarget = switcher.querySelector('.product__watch-switcher__label strong');

        if (labelTarget && itemLabel) {
          labelTarget.textContent = itemLabel;
        }
      }
    });

    const labelTarget = switcher.querySelector('.product__watch-switcher__label strong');
    if (labelTarget && currentLabel) {
      labelTarget.textContent = currentLabel;
    }
  };

  const executeInlineScripts = (root) => {
    if (!root) return;

    root.querySelectorAll('script').forEach((script) => {
      const type = script.getAttribute('type') || 'text/javascript';
      const isExecutable =
        type === 'text/javascript' ||
        type === 'application/javascript' ||
        type === 'module' ||
        type === '';

      if (!isExecutable) return;

      const nextScript = document.createElement('script');
      Array.from(script.attributes).forEach((attribute) => {
        nextScript.setAttribute(attribute.name, attribute.value);
      });
      nextScript.textContent = script.textContent;
      script.replaceWith(nextScript);
    });
  };

  const updateProductInfoDataset = (currentInfo, sourceInfo, productUrl) => {
    const normalizedUrl = normalizeProductUrl(productUrl);

    currentInfo.dataset.productHandle = sourceInfo.dataset.productHandle || '';
    currentInfo.dataset.productId = sourceInfo.dataset.productId || '';
    currentInfo.dataset.url = normalizedUrl.pathname;
    currentInfo.dataset.updateUrl = 'false';
    currentInfo.dataset.enableHistoryState = 'false';
    currentInfo.dataset.featuredProduct = 'true';
    currentInfo.dataset.watchSwitcherEnabled = 'true';
  };

  const updateFormWrapperState = (currentInfo, sourceInfo) => {
    const currentWrapper = currentInfo.querySelector('[data-form-wrapper]');
    const sourceWrapper = sourceInfo.querySelector('[data-form-wrapper]');

    if (!currentWrapper || !sourceWrapper) return;

    currentWrapper.className = sourceWrapper.className;
  };

  const updateEngravingInteractive = async (currentInfo, productUrl) => {
    const currentInteractive = currentInfo.querySelector('[data-engraving-interactive-source]');
    if (!currentInteractive) return;

    let sourceInteractive;

    try {
      sourceInteractive = await getEngravingFragment(productUrl);
    } catch (error) {
      console.warn(error);
      return;
    }

    if (!sourceInteractive) return;

    const nextInteractive = replaceElement(currentInteractive, sourceInteractive);
    executeInlineScripts(nextInteractive);

    const aspectRatio = nextInteractive?.dataset.engravingAspectRatio;
    const flowRoot = currentInfo.querySelector('[data-engraving-flow]');

    if (flowRoot && aspectRatio) {
      flowRoot.style.setProperty('--engraving-preview-aspect-ratio', aspectRatio);
    }
  };

  const updateEngravingModalSummary = (currentInfo, sourceInfo) => {
    const currentSummary = currentInfo.querySelector('[data-engraving-flow] .cart-summary');
    const sourceSummary = sourceInfo.querySelector('[data-engraving-flow] .cart-summary');

    replaceElement(currentSummary, sourceSummary);
  };

  const resetEngravingPreview = (flowRoot) => {
    const canvasContainer = flowRoot.querySelector('.canvas-container');

    if (!canvasContainer) return;

    clearTimeout(canvasContainer._riveFallbackController?.timeoutId);
    canvasContainer._riveFallbackController?.observer?.disconnect();
    canvasContainer._riveFallbackController = null;
    canvasContainer.replaceChildren();
    delete canvasContainer.dataset.riveState;
    delete canvasContainer.dataset.riveInteractiveInitialized;
  };

  const reinitializeEngraving = (currentInfo) => {
    const flowRoot = currentInfo.querySelector('[data-engraving-flow]');
    if (!flowRoot) return;

    const savedInput = flowRoot.querySelector('[data-engraving-saved-value]');
    const savedValue = savedInput?.value || flowRoot.dataset.savedEngraving || '';
    const nextFlowRoot = flowRoot.cloneNode(true);

    nextFlowRoot.dataset.engravingInitialized = 'false';
    nextFlowRoot.dataset.savedEngraving = savedValue;
    nextFlowRoot.dataset.hasSavedEngraving = String(Boolean(savedValue));
    resetEngravingPreview(nextFlowRoot);

    const nextSavedInput = nextFlowRoot.querySelector('[data-engraving-saved-value]');
    if (nextSavedInput) {
      nextSavedInput.value = savedValue;
    }

    flowRoot.replaceWith(nextFlowRoot);

    if (window.BelvansEngravingModal?.initAll) {
      window.BelvansEngravingModal.initAll(nextFlowRoot);
    }
  };

  const refreshProductInfoElement = (productInfo) => {
    productInfo.quantityInput = productInfo.querySelector('[data-quantity-input]');
    productInfo.quantityForm = productInfo.querySelector('.product-form__quantity');
    productInfo.productImages = productInfo.querySelector('product-images');
    productInfo.productMediaList = productInfo.querySelector('[data-product-media-list]');
    productInfo.productFormWrapper = productInfo.querySelector('[data-form-wrapper]');
    productInfo.productForm = productInfo.querySelector('product-form');
    productInfo.variantOptionImages = productInfo.querySelectorAll('[data-variant-option-image]');

    window.Shopify?.PaymentButton?.init?.();
    window.ProductModel?.loadShopifyXR?.();
    window.BelvansKlarnaPromo?.initAll?.(productInfo);

    productInfo.updatePreorderState?.(productInfo);
    productInfo.checkLiveCartInfo?.();
    productInfo.optionImagesWidth?.(productInfo);

    productInfo.dispatchEvent(
      new CustomEvent('theme:product-info:loaded', {
        bubbles: true,
      })
    );
  };

  const applyProductFragment = async (currentInfo, sourceInfo, productUrl) => {
    updateProductInfoDataset(currentInfo, sourceInfo, productUrl);
    updateFormWrapperState(currentInfo, sourceInfo);

    replaceProductImages(currentInfo, sourceInfo);
    replaceBlockBySelector(currentInfo, sourceInfo, '.product__head');
    replaceAllBySelector(currentInfo, sourceInfo, '.block__icon__row');
    replaceBlockBySelector(currentInfo, sourceInfo, '.product__price__wrap');
    replaceBlockBySelector(currentInfo, sourceInfo, '.product__klarna-promo');
    replaceBlockBySelector(currentInfo, sourceInfo, 'variant-selects');
    updateWatchSwitcherState(currentInfo, sourceInfo, currentInfo.dataset.productHandle);
    replaceBlockBySelector(currentInfo, sourceInfo, 'product-form');
    replaceBlockBySelector(currentInfo, sourceInfo, 'pickup-availability');
    replaceAllBySelector(currentInfo, sourceInfo, '.product__block--accordion');

    await updateEngravingInteractive(currentInfo, productUrl);
    updateEngravingModalSummary(currentInfo, sourceInfo);
    reinitializeEngraving(currentInfo);
    refreshProductInfoElement(currentInfo);
  };

  const setBusy = (productInfo, isBusy) => {
    productInfo.toggleAttribute('aria-busy', isBusy);
    productInfo.classList.toggle('is-watch-switching', isBusy);
    productInfo.querySelectorAll('[data-watch-switcher-item]').forEach((item) => {
      item.toggleAttribute('aria-disabled', isBusy);
    });
  };

  const switchProduct = async (productInfo, trigger) => {
    const productUrl = trigger.dataset.productUrl || trigger.getAttribute('href');
    if (!productUrl || productInfo.dataset.watchSwitching === 'true') return;

    productInfo.dataset.watchSwitching = 'true';
    setBusy(productInfo, true);

    try {
      const sourceInfo = await getProductInfoFragment(productUrl, trigger.dataset.productHandle);
      await applyProductFragment(productInfo, sourceInfo, productUrl);
    } catch (error) {
      console.error(error);
      window.location.href = productUrl;
    } finally {
      productInfo.dataset.watchSwitching = 'false';
      setBusy(productInfo, false);
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-watch-switcher-item]');
    if (!trigger) return;

    const productInfo = trigger.closest(
      'product-info[data-featured-product="true"][data-watch-switcher-enabled="true"]'
    );
    if (!productInfo) return;

    event.preventDefault();

    if (trigger.classList.contains('is-active')) return;

    switchProduct(productInfo, trigger);
  });

  window.BelvansProductWatchSwitcher = {
    switchProduct,
    clearCache() {
      productCache.clear();
      engravingCache.clear();
    },
  };
})();
